import React, {useMemo} from "react";
import {observer} from "mobx-react-lite";
import "../../atoms/input/input.sass";
import classNames from "classnames";
import {KeyValue} from "../../../stores/observables/keyValue";
import Select, {OptionTypeBase} from "react-select";
import {MultiVillageSelectObservableList} from "../../../stores/observables/multiVillageSelectObservableList";
import {OptionsType} from "react-select";
import "./multiVillageSelect.sass";
import {useTranslation} from "react-i18next";
import {
  allVillageKeyValue,
  defaultMinMultiSelectRequired,
  independentResidents
} from "../../../shared/constants";
import {IconSource} from "../../../shared/enums/iconSource";
import Icon from "../../atoms/common/icon/icon";
import {CountryCodes} from "../../../shared/enums/countryCodes";
import {AuthUser} from "../../../utils/auth/user";
import {filterVillagesByServicedApartmentAudience} from "../../../shared/interfaces/village";

export interface IMultiSelectInputProps {
  villages?: MultiVillageSelectObservableList;
  isLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  allVillageOptionWarning?: string;
  /**
   * Warning shown when a country-level shortcut ("All Villages"/"All NZ"/"All
   * AU") has been applied while scoped to the "Independent Residents"
   * audience and one or more villages with serviced apartment residents were
   * therefore excluded from the selection.
   */
  servicedApartmentExcludedWarning?: string;
  invalidMinSelectedVillageErrorMessage?: string;
  suppressAllVillageBanner?: boolean;
  /**
   * The currently selected Multi-Village calendar name (e.g. "Independent
   * Residents" or "Independent and Serviced Apartment Residents"). Used to
   * scope the "All Villages"/"All NZ"/"All AU" shortcuts to villages that
   * support that audience, matching the `villages` items list.
   */
  calendarName?: string;
}

const MultiVillageSelect: React.FC<IMultiSelectInputProps> = ({
  villages,
  isLoading,
  disabled,
  placeholder,
  allVillageOptionWarning,
  servicedApartmentExcludedWarning,
  invalidMinSelectedVillageErrorMessage,
  suppressAllVillageBanner = false,
  calendarName
}) => {
  let hasError: boolean = false;
  let villageKeyValues: KeyValue[] = villages ? [...villages.items] : [];
  const villagesByCountry = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(AuthUser.getInstance().villagesByCountry).map(
          ([country, countryVillages]) => [
            country,
            filterVillagesByServicedApartmentAudience(countryVillages, calendarName)
          ]
        )
      ),
    [calendarName]
  );

  const allVillages = useMemo(
    () =>
      Object.values(villagesByCountry).flatMap((villages) =>
        villages.map((village) => new KeyValue(village.id, village.name))
      ),
    [villagesByCountry]
  );

  // True when scoped to the "Independent Residents" audience and one or more
  // villages with serviced apartment residents were filtered out of the
  // country-level shortcuts.
  const hasExcludedServicedApartmentVillages = useMemo(() => {
    if (calendarName !== independentResidents) return false;
    return Object.entries(AuthUser.getInstance().villagesByCountry).some(
      ([country, countryVillages]) =>
        countryVillages.length > (villagesByCountry[country]?.length ?? 0)
    );
  }, [calendarName, villagesByCountry]);

  const selectedVillages: KeyValue[] = villages
    ? [...villages.selectedItems]
    : [];

  const isAllVillageEvent: boolean =
    selectedVillages.length === allVillages.length;
  const {t} = useTranslation(["event", "translation"]);
  const selectPlaceHolder =
    placeholder ?? t("event:placeholders.selectVillagePlaceHolder");

  // When the minimum required selection is not met, we need to show an error.
  if (!isAllVillageEvent) {
    hasError =
      selectedVillages.length <
      (villages?.minRequired ?? defaultMinMultiSelectRequired);
  }

  const villagesPerCountryIsSelected = (country: CountryCodes) => {
    const selectedVillagesInObj = selectedVillages.reduce(
      (o, {label}) => ({...o, [label]: true}),
      {} as {[key: string]: boolean}
    );
    return villagesByCountry[country].every(
      (village) => selectedVillagesInObj[village.name]
    );
  };

  // True when at least one country's villages are all selected, i.e. an "All
  // Villages"/"All NZ"/"All AU" shortcut has been applied.
  const hasFullCountrySelection =
    selectedVillages.length > 0 &&
    Object.values(CountryCodes).some(
      (country) =>
        (villagesByCountry[country]?.length ?? 0) > 0 &&
        villagesPerCountryIsSelected(country)
    );

  // Names of the serviced apartment villages that were excluded from the
  // fully-selected countries, so the user can be told exactly which villages
  // are missing from their selection.
  const excludedVillageNames =
    hasExcludedServicedApartmentVillages && hasFullCountrySelection
      ? Object.entries(AuthUser.getInstance().villagesByCountry).flatMap(
          ([country, countryVillages]) => {
            if (
              (villagesByCountry[country]?.length ?? 0) === 0 ||
              !villagesPerCountryIsSelected(country as CountryCodes)
            ) {
              return [];
            }
            const includedIds = new Set(
              villagesByCountry[country].map((village) => village.id)
            );
            return countryVillages
              .filter((village) => !includedIds.has(village.id))
              .map((village) => village.name);
          }
        ).sort((a, b) => a.localeCompare(b))
      : [];

  if (selectedVillages.length) {
    Object.values(CountryCodes).forEach((country) => {
      if (villagesPerCountryIsSelected(country)) {
        villageKeyValues = villageKeyValues.filter(
          ({value}) => value !== country
        );
      }
    });

    // When all villages are selected then multi village dropdown should be empty.
    if (isAllVillageEvent) {
      villageKeyValues = [];
    }
  }

  const onChange = (option: OptionsType<OptionTypeBase>) => {
    const selectedOptions = option as KeyValue[];
    if (allVillageOptionSelected(selectedOptions)) {
      villages?.setSelectedItems(allVillages);
    } else if (villagesByCountrySelected(selectedOptions)) {
      villages?.setSelectedItems(getSelectedVillagesByCountry(selectedOptions));
    } else {
      villages?.setSelectedItems(selectedOptions);
    }
    villages?.setIsDirty(true);
  };

  function allVillageOptionSelected(keyValues: KeyValue[]): boolean {
    return (
      keyValues.findIndex(
        (value) => value.value === allVillageKeyValue.value
      ) >= 0
    );
  }

  function villagesByCountrySelected(items: KeyValue[]): boolean {
    return !!items.find((items) => items.value in villagesByCountry);
  }

  const getSelectedVillagesByCountry = (keyValues: KeyValue[]): KeyValue[] => {
    const selectedCountry = keyValues.find(
      (value) => value.value in villagesByCountry
    );
    return selectedCountry
      ? villagesByCountry[selectedCountry!.value].map(
          (village) => new KeyValue(village.id, village.name)
        )
      : [];
  };

  return (
    <>
      <div
        className={"multi-village-select edit-field"}
        data-testid="multiVillageSelect"
      >
        <label className={"edit-label"}>
          {t("event:labels.multiVillageSelect")}
        </label>
        <div className={classNames({"is-invalid": hasError})}>
          <Select
            aria-label="select-edit"
            className="typeahead-selection select-input-box"
            classNamePrefix="select"
            value={selectedVillages}
            isMulti
            options={villageKeyValues}
            placeholder={
              <div className="select-placeholder-text">{selectPlaceHolder}</div>
            }
            isDisabled={disabled || false}
            onChange={onChange}
            isLoading={isLoading ?? false}
            closeMenuOnSelect={false}
            noOptionsMessage={() => {
              if (selectedVillages.length > 0 && villageKeyValues.length === 0)
                return null;
              else return t("translation:labels.noOptions");
            }}
            styles={{
              control: (baseStyles, _) => ({
                ...baseStyles,
                borderColor: "#D8E2EE"
              })
            }}
          />
          {hasError && villages?.isDirty && (
            <div className={"edit-validation bottom"}>
              {invalidMinSelectedVillageErrorMessage}
            </div>
          )}
        </div>

        {!suppressAllVillageBanner && isAllVillageEvent && (
          <div className={"all-village-option-warning"}>
            <Icon source={IconSource.info} />
            &nbsp;{allVillageOptionWarning}
            &nbsp;
            <span className={"bold"}>
              {" "}
              All{" "}
              {
                villages
                  ? villages.items.length - 3
                  : 0 /* -3 to ensure we don't count 'All Villages', 'All NZ', and 'All Aus' options. */
              }{" "}
              Villages
            </span>
          </div>
        )}
        {!suppressAllVillageBanner &&
          excludedVillageNames.length > 0 &&
          servicedApartmentExcludedWarning && (
            <div
              className={classNames(
                "all-village-option-warning",
                "excluded-villages"
              )}
            >
              <Icon source={IconSource.info} />
              &nbsp;
              <span>
                {servicedApartmentExcludedWarning}
                <br />
                <span className={"bold"}>
                  {excludedVillageNames.join(", ")}
                </span>
              </span>
            </div>
          )}
      </div>
    </>
  );
};

export default observer(MultiVillageSelect);
